/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package forecasting

import kotlin.math.abs

/**
 * Simple Exponential Smoothing (Single Exponential Smoothing)
 * Best for data with no clear trend or seasonal pattern
 */
class SimpleExponentialSmoothing(
    private val alpha: Double,
) : ForecastingAlgorithm {
    init {
        require(alpha in 0.0..1.0) { "Alpha must be between 0.0 and 1.0" }
    }

    override fun forecast(
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        if (data.isEmpty()) {
            return ForecastingResult(
                algorithmName = "Simple Exponential Smoothing",
                forecastValues = emptyList(),
                confidence = 0.0,
                parameters = mapOf("alpha" to alpha),
            )
        }

        if (data.size == 1) {
            return ForecastingResult(
                algorithmName = "Simple Exponential Smoothing",
                forecastValues = List(periods) { data.first() },
                confidence = 0.3,
                parameters = mapOf("alpha" to alpha, "single_observation" to true),
            )
        }

        // SES implementation matching iOS behavior exactly
        val result = mutableListOf<Double>()
        var smoothed = data[0] // Initialize with first value

        // Start from index 1 and add smoothed values (NOT including the first point)
        // This matches the iOS implementation which starts from index 1
        for (i in 1 until data.size) {
            smoothed = alpha * data[i] + (1 - alpha) * smoothed
            result.add(smoothed)
        }

        // For forecasting: use last SES value (constant projection)
        val lastSES = if (result.isNotEmpty()) result.last() else data[0]
        val forecasts = List(periods) { lastSES }

        // Calculate confidence based on prediction accuracy on historical data
        val errors = mutableListOf<Double>()
        var currentSmooth = data.first()

        for (i in 1 until data.size) {
            val prediction = currentSmooth
            val actual = data[i]
            errors.add(abs(actual - prediction))
            currentSmooth = alpha * actual + (1 - alpha) * currentSmooth
        }

        val meanError = if (errors.isNotEmpty()) errors.average() else 0.0
        val meanValue = data.average()
        val relativeError = if (meanValue != 0.0) meanError / abs(meanValue) else 1.0
        val confidence = (1.0 - relativeError).coerceIn(0.1, 0.95)

        return ForecastingResult(
            algorithmName = "Simple Exponential Smoothing",
            forecastValues = forecasts,
            confidence = confidence,
            historicalValues = result, // Return the computed SES historical values
            parameters =
                mapOf(
                    "alpha" to alpha,
                    "final_smoothed_value" to lastSES,
                    "mean_absolute_error" to meanError,
                    "data_points" to data.size,
                ),
        )
    }
}

/**
 * Linear Trend Regression
 * Uses least-squares regression to fit a linear trend and project it forward
 */
class LinearTrendRegression : ForecastingAlgorithm {
    override fun forecast(
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        if (data.isEmpty()) {
            return ForecastingResult(
                algorithmName = "Linear Trend Regression",
                forecastValues = emptyList(),
                confidence = 0.0,
                parameters = emptyMap(),
            )
        }

        if (data.size == 1) {
            return ForecastingResult(
                algorithmName = "Linear Trend Regression",
                forecastValues = List(periods) { data.first() },
                confidence = 0.3,
                historicalValues = listOf(data.first()),
                parameters = mapOf("single_point" to true),
            )
        }

        // Calculate linear regression: y = mx + b
        val n = data.size
        val x = (0 until n).map { it.toDouble() }
        val y = data

        val sumX = x.sum()
        val sumY = y.sum()
        val sumXY = x.zip(y).sumOf { it.first * it.second }
        val sumXX = x.sumOf { it * it }

        // Calculate slope (m) and intercept (b)
        val slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
        val intercept = (sumY - slope * sumX) / n

        // Generate forecasts
        val forecasts =
            (1..periods).map { period ->
                val futureX = n - 1 + period // Continue from last data point
                intercept + slope * futureX
            }

        // Calculate historical fitted values for trend line
        val historicalFitted =
            x.map { xValue ->
                intercept + slope * xValue
            }

        // Calculate R-squared for confidence
        val meanY = y.average()
        val totalSumSquares = y.sumOf { (it - meanY) * (it - meanY) }
        val residualSumSquares =
            y
                .mapIndexed { index, actual ->
                    val predicted = intercept + slope * index
                    (actual - predicted) * (actual - predicted)
                }.sum()

        val rSquared =
            if (totalSumSquares > 0) {
                1.0 - (residualSumSquares / totalSumSquares)
            } else {
                0.0
            }

        val confidence = rSquared.coerceIn(0.1, 0.95)

        return ForecastingResult(
            algorithmName = "Linear Trend Regression",
            forecastValues = forecasts,
            confidence = confidence,
            historicalValues = historicalFitted,
            parameters =
                mapOf(
                    "slope" to slope,
                    "intercept" to intercept,
                    "r_squared" to rSquared,
                    "data_points" to data.size,
                ),
        )
    }
}
